import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "../../services/Api";
import {
   CButton,
   CCard,
   CCardBody,
   CCol,
   CContainer,
   CForm,
   CFormInput,
   CInputGroup,
   CInputGroupText,
   CRow,
   CAlert,
   CSpinner
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilEnvelopeClosed, cilLockLocked } from "@coreui/icons";
import sap_logo from 'assets/brand/sap_logo.png';

const PasswordReset = () => {
   const navigate = useNavigate();
   const { uid, token } = useParams();

   const [email, setEmail] = useState("");
   const [newPassword, setNewPassword] = useState("");
   const [confirmPassword, setConfirmPassword] = useState("");
   const [message, setMessage] = useState("");
   const [error, setError] = useState("");
   const [loading, setLoading] = useState(false);

   const validatePassword = (password) => {
      const lengthRequirement = password.length >= 8;
      const lowercase = /[a-z]/.test(password);
      const uppercase = /[A-Z]/.test(password);
      const number = /\d/.test(password);
      const specialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
      const conditionsMet = [lowercase, uppercase, number, specialChar].filter(Boolean).length;
      return lengthRequirement && conditionsMet >= 3;
   };

   const handleSendEmail = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError("");
      setMessage("");
      try {
         await apiClient.post("/password-reset/request/", { email });
         setMessage("Um email foi enviado com instruções para redefinir sua password.");
      } catch {
         setError("Erro ao tentar enviar email. Verifique o endereço e tente novamente.");
      } finally {
         setLoading(false);
      }
   };

   const handleResetPassword = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError("");
      setMessage("");

      if (newPassword !== confirmPassword) {
         setError("As passwords não coincidem.");
         setLoading(false);
         return;
      }

      if (!validatePassword(newPassword)) {
         setError("A password deve ter pelo menos 8 caracteres e conter pelo menos 3 dos seguintes: letra minúscula, letra maiúscula, número ou caractere especial.");
         setLoading(false);
         return;
      }

      try {
         await apiClient.post("/password-reset/confirm/", {
            uidb64: uid,
            token,
            new_password: newPassword,
            confirm_password: confirmPassword,
         });
         setMessage("Password alterada com sucesso!");
         setTimeout(() => navigate("/login"), 3000);
      } catch {
         setError("Erro ao redefinir a password. O link pode estar expirado ou inválido.");
      } finally {
         setLoading(false);
      }
   };

   return (
      <div className="bg-body-tertiary min-vh-100 d-flex flex-row align-items-center">
         <CContainer>
            <CRow className="justify-content-center g-0">
               <CCol md={4}>
                  <CCard className="p-4 h-100 border-0 shadow-lg" color="dark" style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>
                     <CCardBody className="d-flex justify-content-center align-items-center h-100">
                        <img
                           src={sap_logo}
                           alt="Logo"
                           style={{ height: 200, width: 200, objectFit: 'contain' }}
                        />
                     </CCardBody>
                  </CCard>
               </CCol>
               <CCol md={8}>
                  <CCard className="p-4 border-0 shadow-lg" style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
                     <CCardBody>
                        {message && <CAlert color="success">{message}</CAlert>}
                        {error && <CAlert color="danger">{error}</CAlert>}

                        {!token || !uid ? (
                           <CForm onSubmit={handleSendEmail}>
                              <h1>Recuperar Password</h1>
                              <p>Insira o seu e-mail para receber um link de redefinição de password.</p>
                              <CInputGroup className="mb-3">
                                 <CInputGroupText>
                                    <CIcon icon={cilEnvelopeClosed} />
                                 </CInputGroupText>
                                 <CFormInput
                                    type="email"
                                    placeholder="Email"
                                    value={email}
                                    required
                                    onChange={(e) => setEmail(e.target.value)}
                                 />
                              </CInputGroup>
                              <CButton color="primary" type="submit" className="w-100" disabled={loading}>
                                 {loading ? (
                                    <>
                                       <CSpinner size="sm" className="me-2" />
                                       A enviar...
                                    </>
                                 ) : (
                                    "Enviar Link"
                                 )}
                              </CButton>
                              <CButton color="secondary" className="w-100 mt-3" onClick={() => navigate('/login')}>
                                 Voltar
                              </CButton>
                           </CForm>
                        ) : (
                           <CForm onSubmit={handleResetPassword}>
                              <h1>Redefinir Password</h1>
                              <p>Defina uma nova password.</p>
                              <CInputGroup className="mb-3">
                                 <CInputGroupText>
                                    <CIcon icon={cilLockLocked} />
                                 </CInputGroupText>
                                 <CFormInput
                                    type="password"
                                    placeholder="Nova password"
                                    value={newPassword}
                                    required
                                    onChange={(e) => setNewPassword(e.target.value)}
                                 />
                              </CInputGroup>
                              <CInputGroup className="mb-3">
                                 <CInputGroupText>
                                    <CIcon icon={cilLockLocked} />
                                 </CInputGroupText>
                                 <CFormInput
                                    type="password"
                                    placeholder="Confirmar password"
                                    value={confirmPassword}
                                    required
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                 />
                              </CInputGroup>
                              <CButton color="primary" type="submit" className="w-100" disabled={loading}>
                                 {loading ? (
                                    <>
                                       <CSpinner size="sm" className="me-2" />
                                       A alterar...
                                    </>
                                 ) : (
                                    "Alterar password"
                                 )}
                              </CButton>
                              <CButton color="secondary" className="w-100 mt-3" onClick={() => navigate('/login')}>
                                 Voltar
                              </CButton>
                           </CForm>
                        )}
                     </CCardBody>
                  </CCard>
               </CCol>
            </CRow>
         </CContainer>
      </div>
   );
};

export default PasswordReset;
